// Scripted "Submit Enquiry" flow — ported 1:1 from the old tripplanner.js

const CITIES = ["Gangtok", "Darjeeling", "Sikkim", "Shillong", "Kalimpong", "Tawang"];

export const DEST_GROUPS = {
  Romantic: CITIES, Family: CITIES, Solo: CITIES, Friends: CITIES, Group: CITIES,
  Honeymoon: CITIES, Adventure: CITIES, Relaxation: CITIES, Cultural: CITIES, Wildlife: CITIES,
};

export const PICKUP_POINTS = {
  Darjeeling: ["Darjeeling Mall", "Chowrasta", "Ghoom Railway Station", "Lebong", "Rockville Road"],
  Siliguri: ["Bagdogra Airport", "Siliguri Junction", "Sevoke Road", "Hill Cart Road"],
  Gangtok: ["MG Marg", "Deorali", "Tadong", "Ranka Taxi Stand"],
};

export const ALL_DESTS = [...new Set(Object.values(DEST_GROUPS).flat())];

export const AI_PLACEHOLDER = "Ask about tours, cabs, hotels, wellness...";

export const AI_WELCOME_TEXT =
  "Namaste! I am Aria, your TGH AI Travel Consultant.\n\n" +
  "I can help you discover and book:\n\n" +
  "- **Tour Packages** — Spiti, Himachal, spiritual journeys and more\n" +
  "- **Cab Rides** — Shared seats or private cars across North and East India\n" +
  "- **Hotels and Homestays** — Handpicked mountain stays\n" +
  "- **Wellness Retreats** — Yoga and Ayurvedic detox programs\n\n" +
  "Where would you like to travel?";

// Quick chips under the input (same set as the full-page chatbot)
export const AI_SUGGESTIONS = [
  "⚡ Next 30 Days Packages",
  "🏔️ Gangtok & Darjeeling Itinerary",
  "🚗 Bagdogra to Gangtok Cabs",
  "🏨 Boutique Stays in Sikkim",
  "⭐ Top Monasteries in Sikkim",
  "🧘 Wellness retreats",
];

// Starter cards on the AI welcome screen (same as the full-page chatbot)
export const AI_STARTERS = [
  { icon: "⚡", label: "Departures in Next 30 Days", desc: "Confirmed group departures & filling-fast seats",
    query: "What packages and confirmed group departures do you have in the next 30 days?" },
  { icon: "🏔️", label: "Sikkim & Darjeeling Guide", desc: "Scenic 5-day itineraries with top sights & monasteries",
    query: "Plan a scenic 5-day tour to Gangtok & Darjeeling with top sights and places to visit" },
  { icon: "🚗", label: "Mountain Cabs from Bagdogra", desc: "Verified cabs, SUVs & shared mountain rides",
    query: "Check cab availability and rates from Bagdogra to Gangtok with vehicle options" },
  { icon: "🏨", label: "Boutique Mountain Stays", desc: "Handpicked valley & riverside cottages in Sikkim and Manali",
    query: "Recommend handpicked luxury boutique stays in Sikkim" },
];

export const FLOW = [
  { id: "entryMode", bot: "Planning a trip with us? I'm here to make it easy. How would you like to get started today?",
    type: "buttons", options: ["Submit Enquiry", "Chat with AI Agent"],
    nextMap: { "Submit Enquiry": "helpType", "Chat with AI Agent": "aiDirect" } },
  { id: "helpType", bot: "Perfect! What can I assist you with right now?", type: "buttons",
    options: ["Cabs", "Tours", "Stays", "Retreats"],
    nextMap: { Cabs: "cabFromCity", Trip: "tripType", Tours: "tripType", Stays: "hotelPropertyType", Retreats: "retreatType" } },
  { id: "aiDirect", type: "ai" },

  // Cabs
  { id: "cabFromCity", bot: "From which city are you travelling?", type: "input-with-suggestions", next: "cabPickupPoint" },
  { id: "cabPickupPoint", bot: "Select your pickup point", type: "pickup", citykey: "cabFromCity", next: "cabToCity" },
  { id: "cabToCity", bot: "Where are you travelling to?", type: "input-with-suggestions", next: "cabDropPoint" },
  { id: "cabDropPoint", bot: "Select your drop point", type: "drop", citykey: "cabToCity", next: "cabDate" },
  { id: "cabDate", bot: "When is your journey date?", type: "date", next: "cabVehicleType" },
  { id: "cabVehicleType", bot: "Which vehicle type do you prefer?", type: "buttons",
    options: ["Luxury", "Standard", "Economy"], next: "collectName" },

  // Tours
  { id: "tripType", bot: "Nice choice! What type of trip are you planning?", type: "buttons",
    options: Object.keys(DEST_GROUPS), next: "askDestination" },
  { id: "askDestination", bot: "Awesome! Where are you planning to travel?", type: "input-with-suggestions", next: "dateFixed" },
  { id: "dateFixed", bot: "Have you decided your travel dates yet?", type: "buttons", options: ["Yes", "Not Yet"], next: "chooseDate" },
  { id: "chooseDate", bot: "Great! Please choose your preferred travel date", type: "date", next: "tripDays" },
  { id: "tripDays", bot: "How long would you like your trip to be?", type: "buttons-with-input",
    options: ["3 Days", "5 Days", "7 Days", "9 Days"], placeholder: "e.g. 5 days", next: "budget" },
  { id: "budget", bot: "What budget range are you comfortable with per person?", type: "buttons",
    options: ["₹10k-20k", "₹20k-40k", "₹40k-70k", "₹70k+"], next: "collectName" },

  // Stays
  { id: "hotelPropertyType", bot: "What kind of stay are you looking for?", type: "buttons",
    options: ["Hotel", "Homestay", "Resort", "Villa", "Farmstay", "Guest House", "Cottage", "Apartment"], next: "hotelCity" },
  { id: "hotelCity", bot: "Great choice! Where would you like to stay?", type: "input-with-suggestions", next: "hotelCheckIn" },
  { id: "hotelCheckIn", bot: "When will you be checking in?", type: "date", next: "hotelCheckOut" },
  { id: "hotelCheckOut", bot: "What will be your check-out date?", type: "date", next: "hotelGuests" },
  { id: "hotelGuests", bot: "How many guests will be staying?", type: "buttons-with-input",
    options: ["1 Guest", "2 Guests", "3 Guests", "4 Guests"], placeholder: "e.g. 3 guests", next: "collectPhone" },

  // Retreats
  { id: "retreatType", bot: "What kind of retreat are you looking for?", type: "buttons",
    options: ["Wellness", "Yoga", "Meditation", "Digital Detox"], next: "retreatLocation" },
  { id: "retreatLocation", bot: "Where would you like to attend the retreat?", type: "input-with-suggestions", next: "retreatDate" },
  { id: "retreatDate", bot: "When are you planning for the retreat?", type: "date", next: "retreatDuration" },
  { id: "retreatDuration", bot: "How many days would you prefer?", type: "buttons-with-input",
    options: ["3 Days", "5 Days", "7 Days"], placeholder: "e.g. 5 days", next: "collectName" },

  // Common
  { id: "collectName", bot: "Your name?", type: "input", next: "collectPhone" },
  { id: "collectPhone", bot: "Your WhatsApp number?", type: "input", next: "collectEmail" },
  { id: "collectEmail", bot: "Your email (optional)", type: "input", optional: true, next: "summary" },
  { id: "summary", type: "summary" },

  // After the summary
  { id: "again", bot: "Would you like to submit another enquiry?", type: "buttons",
    options: ["Yes", "No"], nextMap: { Yes: "__restart", No: "__end" } },
];

export const FLOW_MAP = Object.fromEntries(FLOW.map((s) => [s.id, s]));

export function buildSummaryRows(a) {
  const d = (v) => v || "-";
  switch (a.helpType) {
    case "Cabs":
      return [["Service", "Cab Booking"], ["From", d(a.cabFromCity)], ["Pickup Point", d(a.cabPickupPoint)],
        ["To", d(a.cabToCity)], ["Drop Point", d(a.cabDropPoint)], ["Date", d(a.cabDate)],
        ["Vehicle Type", d(a.cabVehicleType)], ["Name", d(a.collectName)], ["Phone", d(a.collectPhone)]];
    case "Trip":
    case "Tours":
      return [["Trip Type", d(a.tripType)], ["Destination", d(a.askDestination)], ["Date", d(a.chooseDate)],
        ["Duration", d(a.tripDays)], ["Budget", d(a.budget)], ["Name", d(a.collectName)],
        ["Phone", d(a.collectPhone)], ["Email", a.collectEmail || "Not provided"]];
    case "Stays":
      return [["Service", "Stay Booking"], ["City", d(a.hotelCity)], ["Check-in", d(a.hotelCheckIn)],
        ["Check-out", d(a.hotelCheckOut)], ["Guests", d(a.hotelGuests)], ["Property Type", d(a.hotelPropertyType)],
        ["Phone", d(a.collectPhone)]];
    case "Retreats":
      return [["Service", "Retreat"], ["Type", d(a.retreatType)], ["Location", d(a.retreatLocation)],
        ["Date", d(a.retreatDate)], ["Duration", d(a.retreatDuration)], ["Phone", d(a.collectPhone)]];
    default:
      return [];
  }
}

export function buildLeadPayload(a, fbp, fbc) {
  const lead = {
    source: "trip-planner-widget",
    helpType: a.helpType,
    name: a.collectName || "N/A",
    phone: a.collectPhone || "N/A",
    email: a.collectEmail || "N/A",
    fbp,
    fbc,
  };
  if (a.helpType === "Cabs") {
    lead.destination = `Pickup: ${a.cabFromCity} → ${a.cabToCity}`;
    lead.date = a.cabDate;
  } else if (a.helpType === "Trip" || a.helpType === "Tours") {
    lead.tripType = a.tripType;
    lead.destination = a.askDestination;
    lead.date = a.chooseDate;
    lead.days = a.tripDays;
    lead.budget = a.budget;
  }
  return lead;
}